import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Cycles through the concern's signs, one at a time, inside the hero.
 *
 * Read-only content: the visitor already knows which signs describe them, so there is nothing to
 * tick and nothing to submit. The arrows navigate, they don't record anything.
 *
 * Every item stays in the DOM and in the accessibility tree, so screen readers get the whole
 * list — the rotation is a way of showing a six-item list in one line's height, not a way of
 * hiding content. With no JS the plain stacked list is what renders, since the CSS only stacks
 * items and reveals the arrows once this component sets `data-rotating`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [items] - The sign items, in order.
 *
 * @extends {Component<Refs>}
 */
class ConcernSigns extends Component {
  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #index = 0;
  #visible = true;
  /** Set once the visitor uses an arrow — automatic advancing never resumes after that. */
  #engaged = false;

  connectedCallback() {
    super.connectedCallback();

    // One sign is not a carousel.
    if (this.#items.length < 2) return;

    this.dataset.rotating = 'true';
    this.#show(0);

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#visible = entry.isIntersecting;
        this.#sync();
      },
      { threshold: 0 }
    );
    this.#observer.observe(this);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** Wired up declaratively with `on:click="/next"`. */
  next() {
    this.#engaged = true;
    this.#stop();
    this.#show(this.#index + 1);
  }

  /** Wired up declaratively with `on:click="/previous"`. */
  previous() {
    this.#engaged = true;
    this.#stop();
    this.#show(this.#index - 1);
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return this.refs.items ?? [];
  }

  /** @param {number} index - Wraps around both ends. */
  #show(index) {
    const items = this.#items;
    if (!items.length) return;

    this.#index = ((index % items.length) + items.length) % items.length;

    for (const [position, item] of items.entries()) {
      if (position === this.#index) item.dataset.active = 'true';
      else delete item.dataset.active;
    }
  }

  /**
   * Runs the timer only while the hero is on screen, and never for someone who has asked for
   * reduced motion — they still get the arrows, just nothing that moves on its own.
   */
  #sync() {
    if (this.#engaged || !this.#visible || prefersReducedMotion()) {
      this.#stop();
      return;
    }

    if (this.#timer) return;

    const interval = Number(this.dataset.interval) || 3000;
    this.#timer = setInterval(() => this.#show(this.#index + 1), interval);
  }

  #stop() {
    if (!this.#timer) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get('concern-signs-component')) {
  customElements.define('concern-signs-component', ConcernSigns);
}
